import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import axios from 'axios';
import { Loader2, Film, Play, RefreshCw, Trash2, ChevronDown } from 'lucide-react';
import PublicLayout from '../../components/public/PublicLayout';
import { authSelector } from '../../features/auth/slice';
import SEO from '../../components/SEO';
import { PUBLIC_BASE } from './config';

const PROGRESS_STEPS = [
  { label: 'YouTube 인기차트 수집', duration: 15 },
  { label: 'p!nk MV 검색', duration: 300 },
  { label: '저장', duration: 60 },
];
const TOTAL_ESTIMATED = PROGRESS_STEPS.reduce((sum, s) => sum + s.duration, 0);

function getWeekLabel() {
  const now = new Date();
  const weekOfMonth = Math.ceil(now.getDate() / 7);
  return `${now.getMonth() + 1}월 ${weekOfMonth}주차`;
}

function formatDuration(sec) {
  if (!sec) return '';
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
}

function ProgressBar({ elapsed }) {
  const progress = Math.min((elapsed / TOTAL_ESTIMATED) * 100, 95);
  let currentStep = 0;
  let accumulated = 0;
  for (let i = 0; i < PROGRESS_STEPS.length; i++) {
    accumulated += PROGRESS_STEPS[i].duration;
    if (elapsed < accumulated) { currentStep = i; break; }
    if (i === PROGRESS_STEPS.length - 1) currentStep = i;
  }

  return (
    <div className="w-full max-w-sm mt-6">
      <div className="flex justify-between text-[11px] text-slate-500 mb-2">
        <span>{PROGRESS_STEPS[currentStep].label}...</span>
        <span>{Math.floor(elapsed / 60)}:{String(Math.floor(elapsed % 60)).padStart(2, '0')} 경과</span>
      </div>
      <div className="w-full h-1.5 bg-white/5 rounded-full overflow-hidden">
        <div
          className="h-full bg-gradient-to-r from-sky-500 to-blue-500 rounded-full transition-all duration-1000 ease-linear"
          style={{ width: `${progress}%` }}
        />
      </div>
      <div className="flex justify-between mt-2 gap-1">
        {PROGRESS_STEPS.map((step, i) => (
          <div key={step.label} className="flex-1 text-center">
            <div className={`h-0.5 rounded-full mb-1 ${i <= currentStep ? 'bg-sky-500/40' : 'bg-white/5'}`} />
            <span className={`text-[9px] ${i <= currentStep ? 'text-sky-400/70' : 'text-slate-600'}`}>
              {step.label}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

function MusicVideoCard({ mv, isAdmin, onDelete }) {
  const navigate = useNavigate();

  const handleDelete = (e) => {
    e.stopPropagation();
    if (window.confirm(`"${mv.title}" 뮤직비디오를 삭제하시겠습니까?`)) {
      onDelete(mv.id);
    }
  };

  return (
    <div
      onClick={() => navigate(`${PUBLIC_BASE}/music-videos/${mv.id}`)}
      className="group flex flex-col bg-[#182334] rounded-xl overflow-hidden border border-white/5 cursor-pointer hover:border-white/15 transition-all hover:shadow-xl hover:shadow-black/20">
      {/* 썸네일 */}
      <div className="relative w-full" style={{ paddingTop: '56.25%' }}>
        <img
          src={mv.thumbnail}
          alt={mv.title}
          className="absolute inset-0 w-full h-full object-cover"
        />
        {/* Play 오버레이 */}
        <div className="absolute inset-0 flex items-center justify-center bg-black/0 group-hover:bg-black/40 transition-colors">
          <div className="w-10 h-10 rounded-full bg-white/90 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity shadow-lg">
            <Play size={18} className="text-black ml-0.5" fill="black" />
          </div>
        </div>
        {/* Duration 뱃지 */}
        {mv.duration && (
          <span className="absolute bottom-2 right-2 bg-black/70 text-white text-[10px] px-1.5 py-0.5 rounded font-medium">
            {formatDuration(mv.duration)}
          </span>
        )}
        {/* Source 뱃지 */}
        {mv.source === 'internal' ? (
          <span className="absolute top-2 left-2 inline-flex items-center gap-1.5 text-[11px] font-semibold px-2.5 py-0.5 rounded-full bg-sky-500/15 text-sky-400 backdrop-blur-sm">
            <img src="https://distribution-pink.s3.ap-northeast-3.amazonaws.com/dev/images/logo.png" alt="p!nk" className="h-[11px] w-auto" />
          </span>
        ) : (
          <span className="absolute top-2 left-2 inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded-full bg-red-500/15 text-red-400 backdrop-blur-sm">
            <svg viewBox="0 0 28 20" className="w-[16px] h-[12px]" fill="currentColor"><path d="M27.9727 3.12324C27.6435 1.89323 26.6768 0.926623 25.4468 0.597366C23.2197 2.24288e-07 14.285 0 14.285 0C14.285 0 5.35042 2.24288e-07 3.12323 0.597366C1.89323 0.926623 0.926623 1.89323 0.597366 3.12324C2.24288e-07 5.35042 0 10 0 10C0 10 2.24288e-07 14.6496 0.597366 16.8768C0.926623 18.1068 1.89323 19.0734 3.12323 19.4026C5.35042 20 14.285 20 14.285 20C14.285 20 23.2197 20 25.4468 19.4026C26.6768 19.0734 27.6435 18.1068 27.9727 16.8768C28.5701 14.6496 28.5701 10 28.5701 10C28.5701 10 28.5677 5.35042 27.9727 3.12324Z"/><path d="M11.4253 14.2854L18.8477 10.0004L11.4253 5.71533V14.2854Z" fill="#FF0000"/></svg>
            YouTube
          </span>
        )}
        {/* 삭제 버튼 */}
        {isAdmin && (
          <button
            onClick={handleDelete}
            className="absolute bottom-2 left-2 p-1.5 bg-black/50 hover:bg-red-500/80 rounded-lg opacity-0 group-hover:opacity-100 transition-all"
            title="삭제">
            <Trash2 size={12} className="text-white" />
          </button>
        )}
      </div>

      {/* 텍스트 */}
      <div className="p-3 flex-1 flex flex-col min-w-0">
        <h3 className="text-white text-xs sm:text-sm font-semibold line-clamp-2 mb-1 group-hover:text-sky-300 transition-colors leading-snug">
          {mv.title}
        </h3>
        <p className="text-slate-500 text-[11px] sm:text-xs truncate mt-auto">
          {mv.artist}
        </p>
      </div>
    </div>
  );
}

function PublicMusicVideoPage() {
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const timerRef = useRef(null);
  const pollRef = useRef(null);
  const { me } = useSelector(authSelector.selectAuthState);
  const isAdmin = me?.adminYn === 'Y';

  const fetchVideos = useCallback((offset = 0, limit = 16) => {
    return axios.get('/public/music-videos', { params: { offset, limit } }).then((res) => res.data);
  }, []);

  const startTimer = useCallback(() => {
    setElapsed(0);
    timerRef.current = setInterval(() => setElapsed((e) => e + 1), 1000);
  }, []);

  const stopTimer = useCallback(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const stopPolling = useCallback(() => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  }, []);

  useEffect(() => {
    return () => { stopTimer(); stopPolling(); };
  }, [stopTimer, stopPolling]);

  useEffect(() => {
    fetchVideos()
      .then((data) => {
        if (data.items?.length === 0) {
          setGenerating(true);
          startTimer();
          let pollElapsed = 0;
          pollRef.current = setInterval(() => {
            pollElapsed += 10000;
            fetchVideos()
              .then((d) => {
                if (d.items?.length > 0) {
                  setVideos(d.items);
                  setHasMore(d.hasMore || false);
                  setGenerating(false);
                  stopTimer();
                  stopPolling();
                } else if (pollElapsed >= 600000) {
                  setGenerating(false);
                  stopTimer();
                  stopPolling();
                }
              })
              .catch(() => {});
          }, 10000);
        } else {
          setVideos(data.items || []);
          setHasMore(data.hasMore || false);
        }
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [fetchVideos, startTimer, stopTimer, stopPolling]);

  const handleLoadMore = () => {
    setLoadingMore(true);
    fetchVideos(videos.length, 16)
      .then((data) => {
        setVideos((prev) => [...prev, ...(data.items || [])]);
        setHasMore(data.hasMore || false);
      })
      .catch(() => {})
      .finally(() => setLoadingMore(false));
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`/public/music-videos/${id}`);
      setVideos((prev) => prev.filter((v) => v.id !== id));
    } catch {
      alert('삭제에 실패했습니다.');
    }
  };

  const handleRegenerate = async () => {
    if (!window.confirm('뮤직비디오를 새로 생성하시겠습니까?\n(48개 생성, 약 3~5분 소요)')) return;
    setGenerating(true);
    startTimer();
    setVideos([]);
    try {
      await axios.post('/public/music-videos/regenerate');
      let pollElapsed = 0;
      pollRef.current = setInterval(() => {
        pollElapsed += 10000;
        fetchVideos()
          .then((data) => {
            if (data.items?.length > 0) {
              setVideos(data.items);
              setHasMore(data.hasMore || false);
              setGenerating(false);
              stopTimer();
              stopPolling();
            } else if (pollElapsed >= 600000) {
              setGenerating(false);
              stopTimer();
              stopPolling();
            }
          })
          .catch(() => {});
      }, 10000);
    } catch {
      setGenerating(false);
      stopTimer();
    }
  };

  return (
    <PublicLayout>
      <SEO
        title="뮤직비디오"
        description="인기 뮤직비디오와 p!nk 아티스트 뮤직비디오를 감상하세요."
        path="/music-videos"
      />
      <div className="max-w-6xl mx-auto px-4 sm:px-6 pt-8 pb-10">
        {/* 헤더 */}
        <h1 className="text-xl sm:text-2xl font-bold text-white tracking-tight text-center mb-2">뮤직비디오</h1>
        <div className="flex items-center justify-center gap-3 mb-1">
          <span className="inline-flex items-center px-3 py-1 rounded-full bg-sky-500/10 border border-sky-500/20 text-sky-400 text-xs font-medium">
            {getWeekLabel()}
          </span>
        </div>
        <p className="text-slate-500 text-sm text-center mb-8">최신 뮤직비디오를 만나보세요</p>
        <div className="flex items-end justify-end mb-8">
          {isAdmin && !generating && (
            <button
              onClick={handleRegenerate}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs text-slate-400 hover:text-sky-400 bg-white/5 hover:bg-white/10 border border-white/10 rounded-lg transition-colors">
              <RefreshCw size={12} />
              재생성
            </button>
          )}
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20 gap-3 text-slate-400">
            <Loader2 size={20} className="animate-spin" />
            <span>로딩 중...</span>
          </div>
        ) : generating ? (
          <div className="flex flex-col items-center justify-center py-20 gap-4 text-slate-400">
            <div className="w-12 h-12 rounded-full border-2 border-sky-500 border-t-transparent animate-spin" />
            <p className="text-sm">뮤직비디오를 수집하고 있습니다...</p>
            <p className="text-xs text-slate-600">YouTube 인기차트 + p!nk MV 검색, 약 3~5분 소요됩니다</p>
            <ProgressBar elapsed={elapsed} />
          </div>
        ) : videos.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20 gap-3 text-slate-500">
            <Film size={40} />
            <p>뮤직비디오가 없습니다.</p>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4">
              {videos.map((mv) => (
                <MusicVideoCard
                  key={mv.id}
                  mv={mv}
                  isAdmin={isAdmin}
                  onDelete={handleDelete}
                />
              ))}
            </div>

            {hasMore && (
              <div className="flex justify-center mt-8">
                <button
                  onClick={handleLoadMore}
                  disabled={loadingMore}
                  className="flex items-center gap-2 px-6 py-2.5 bg-white/5 hover:bg-white/10 border border-white/10 rounded-full text-slate-300 text-sm transition-colors disabled:opacity-50">
                  {loadingMore ? (
                    <Loader2 size={14} className="animate-spin" />
                  ) : (
                    <ChevronDown size={14} />
                  )}
                  더보기
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </PublicLayout>
  );
}

export default PublicMusicVideoPage;
